import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Icon } from "@vds/icons";
import { useCortexStream } from "../../global/chatbot/useCortexStream";
import {
  clearCortexHistory,
  clearStreamNotification,
} from "../../store/actions/chatbot-actions";
import {
  toggleAssistantPanel,
  addRecentPrompt,
  toggleFavoritePrompt,
  patchCustomdata,
  fetchAssistantPrompts,
} from "../../store/actions/assistantPrompts-actions";
import SparkleIcon from "../../global/chatbot/SparkleIcon";
import UserMessage from "./components/UserMessage";
import AssistantMessage from "./components/AssistantMessage";
import ChatInput from "./components/ChatInput";
import SuggestionChips from "./components/SuggestionChips";
import PromptHistoryPanel from "./components/PromptHistoryPanel";
import { getUserCustomdataEndpoint } from "../../configuration";
import { getSessionStorageData } from "../../global/utility/Utils";
import { STORAGE } from "../../global/constants/storageConstants";

// ─── Styled Components ────────────────────────────────────────────────────────

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  overflow: hidden;
`;

const ContentRow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 0;
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const SidebarWrapper = styled.div`
  width: ${({ $open }) => ($open ? "320px" : "0")};
  flex-shrink: 0;
  overflow: hidden;
  height: 100%;
  transition: width 0.3s ease-in-out;

  @media (max-width: 768px) {
    width: 0;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 6px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f5f5f5;
  flex-shrink: 0;
`;

const MenuWrapper = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  color: #6b7280;
  border-radius: 4px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
    color: #374151;
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  min-width: 190px;
  z-index: 100;
  overflow: hidden;
`;

const MenuItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  padding: 10px 16px;
  text-align: left;
  font-size: 14px;
  color: ${({ disabled }) => (disabled ? "#9ca3af" : "#1a1a1a")};
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background-color 0.1s ease;

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scroll-behavior: smooth;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 24px;
  padding-top: 48px;
  padding-bottom: 48px;
`;

const IconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #cd040b 0%, #ea580c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Greeting = styled.h1`
  font-size: 44px;
  font-weight: 300;
  color: #1a1a1a;
  margin: 0;
  letter-spacing: -0.5px;
  line-height: 1.1;
`;

const SubGreeting = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  font-weight: 400;
`;

const MessagesEndAnchor = styled.div``;

// ─── Component ────────────────────────────────────────────────────────────────

const EMPTY_FAVORITES = [];

export default function Assistant() {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const pageContainerRef = useRef(null);

  // Keep the container height exactly from its top edge to the bottom of the
  // viewport. This ensures ChatInput stays visible even when banners above
  // (CortexBar, CalibrationBanner) push the container down.
  useLayoutEffect(() => {
    const el = pageContainerRef.current;
    if (!el) return;
    const fit = () => {
      el.style.height = `${window.innerHeight - el.getBoundingClientRect().top}px`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  });

  const cortexMessages = useSelector(
    (state) => state.chatbot.cortexMessages.messages
  );

  const session = useSelector(
    (state) => state.sessionReducer?.activeSession?.activeSession
  );

  const userDetails = getSessionStorageData(STORAGE.ACCOUNT_DETAILS);
  const displayName = [userDetails?.firstname, userDetails?.lastname].filter(Boolean).join(" ");

  const isPanelOpen = useSelector((state) => state.assistantPrompts?.isPanelOpen);
  const favorites = useSelector((state) => state.assistantPrompts?.favorites ?? EMPTY_FAVORITES);
  const pendingText = useSelector((state) => state.assistantPrompts?.pendingText ?? null);

  const favoritesEnabled = window.REACT_APP_FEATURE_PROMPT_FAVORITES === "enabled";

  // On mount, fetch favorites from backend (source of truth).
  // Redux-persist provides an instant local cache while the fetch completes.
  useEffect(() => {
    if (!favoritesEnabled) return;
    dispatch(fetchAssistantPrompts());
  }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const accountId = session?.accountId;
  const userId = session?.userId || accountId;
  const favoritesSet = new Set(favorites.map((f) => f.text));

  const handleToggleFavorite = (text) => {
    dispatch(toggleFavoritePrompt(text));
    const willAdd = !favoritesSet.has(text);
    const current = favorites.map((f) => [f.id, f.text]);
    let next;
    if (willAdd) {
      const id = String(Date.now());
      next = current.length < 10 ? [...current, [id, text]] : [...current.slice(1), [id, text]];
    } else {
      next = current.filter(([, t]) => t !== text);
    }
    dispatch(
      patchCustomdata({
        url: getUserCustomdataEndpoint(),
        method: "patch",
        data: {
          accountname: accountId,
          id: userId,
          customdata: { assistant_prompts: Object.fromEntries(next) },
        },
      })
    );
  };

  const { streamingMessage, sendMessage, stopStream } = useCortexStream();
  const reduxIsStreaming = useSelector((state) => state.chatbot.isStreaming);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [cortexMessages, streamingMessage.content]);

  // Clear any pending stream notification when the user views the Assistant page.
  useEffect(() => {
    dispatch(clearStreamNotification());
  }, [dispatch]);

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text || streamingMessage.isStreaming) return;

    setInputValue("");

    const history = cortexMessages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));

    sendMessage(text, history);

    if (favoritesEnabled) {
      dispatch(addRecentPrompt(text));
    }
  };

  const handleSuggestionSelect = (text) => {
    setInputValue(text);
  };

  const handleClear = () => {
    dispatch(clearCortexHistory());
  };

  const isStreaming = streamingMessage.isStreaming || reduxIsStreaming;
  const hasMessages = cortexMessages.length > 0;

  return (
    <PageContainer ref={pageContainerRef}>
      <ContentRow>
        <MainArea>
          <Toolbar>
            <MenuWrapper ref={menuRef}>
              <MenuButton
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="menu"
                aria-expanded={menuOpen}
              >
                <Icon name="menu" size="medium" />
              </MenuButton>
              {menuOpen && (
                <MenuDropdown>
                  {favoritesEnabled && (
                    <MenuItem
                      onClick={() => {
                        dispatch(toggleAssistantPanel());
                        setMenuOpen(false);
                      }}
                    >
                      <Icon name="history" size="small" />
                      History
                    </MenuItem>
                  )}
                  <MenuItem
                    disabled={!hasMessages}
                    onClick={() => {
                      if (!hasMessages) return;
                      handleClear();
                      setMenuOpen(false);
                    }}
                  >
                    <Icon name="trash" size="small" color={hasMessages ? "#1a1a1a" : "#9ca3af"} />
                    Clear conversation
                  </MenuItem>
                </MenuDropdown>
              )}
            </MenuWrapper>
          </Toolbar>

          <MessagesArea>
            {!hasMessages && !isStreaming ? (
              <EmptyState>
                <IconCircle>
                  <SparkleIcon size={28} />
                </IconCircle>
                <Greeting data-testid="assistant-greeting">
                  Hello{displayName ? `, ${displayName}` : ""}!
                </Greeting>
                <SubGreeting>
                  How can I help you with Video Insights today?
                </SubGreeting>
                <SuggestionChips onSelect={handleSuggestionSelect} />
              </EmptyState>
            ) : (
              <>
                {cortexMessages.map((msg) =>
                  msg.role === "user" ? (
                    <UserMessage
                      key={msg.id}
                      message={msg}
                      onToggleFavorite={favoritesEnabled ? handleToggleFavorite : undefined}
                      isFavorited={favoritesEnabled ? favoritesSet.has(msg.content) : false}
                      isPending={favoritesEnabled && pendingText === msg.content}
                    />
                  ) : (
                    <AssistantMessage key={msg.id} streamingMessage={msg} />
                  )
                )}
                {isStreaming && (
                  <AssistantMessage
                    streamingMessage={
                      streamingMessage.isStreaming
                        ? streamingMessage
                        : { ...streamingMessage, isStreaming: true, processingState: "thinking" }
                    }
                  />
                )}
              </>
            )}
            <MessagesEndAnchor ref={messagesEndRef} />
          </MessagesArea>

          <ChatInput
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onSend={handleSend}
            onStop={stopStream}
            isProcessing={isStreaming}
          />
        </MainArea>

        {favoritesEnabled && (
          <SidebarWrapper $open={isPanelOpen}>
            {isPanelOpen && (
              <PromptHistoryPanel onSelectPrompt={(text) => setInputValue(text)} />
            )}
          </SidebarWrapper>
        )}
      </ContentRow>
    </PageContainer>
  );
}
